import React, { CSSProperties } from 'react';
import mustache from 'mustache';
import { PickupLocation } from '../../../../../../__generated__/loma';
import { useSettings } from '../../../../hooks/useSettings';
import { useTranslations } from '../../../../hooks/useTranslations';
import { LayoutTree } from '../../DynamicLayout';
import { MediumHeader } from '../../../ui/Header/Header';
import MaptilerMap from './MaptilerMap/MaptilerMap';
import TextLink from '../../../ui/TextLink/TextLink';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
} from '../../../../hooks/Analytics';
import { useTheme } from '../../../../hooks/useTheme';
import PickupLocationHoursDisclosure from './PickupLocationHoursDisclosure';
import styles from './PickupLocationDetails.module.css';

const { FeatureLayout } = LayoutTree;

const mapStylesLandscape: CSSProperties = {
  flex: '1 0 50%',
  alignItems: 'stretch',
  maxWidth: '25rem',
};

const mapStylesPortrait: CSSProperties = {
  flexDirection: 'column',
  height: '12.5rem',
  width: '100%',
};

export interface PickupLocationDetailsProps {
  layout?: LayoutTree.FeatureLayout;
  featureName?: string;
  pickupLocation: PickupLocation;
}

const PickupLocationDetails = ({
  featureName,
  layout = FeatureLayout.PORTRAIT,
  pickupLocation,
}: PickupLocationDetailsProps) => {
  const logEvent = useAnalyticsPost();
  const landscape = layout === FeatureLayout.LANDSCAPE;

  const {
    bodytextDefaultNone: textStyle,
    linksNoneDefault: linkDefault,
    linksNoneHover: linkHover,
    linksTrackingDefault: linkSecondaryDefault,
    linksTrackingHover: linkSecondaryHover,
    panelbodyNoneNone: panelbody,
  } = useTheme();

  const { pickupLocation: pickupLocationSettings } = useSettings();

  const {
    moreInfoLink,
    showMoreInfoLink: showMoreInfoLinkRaw,
    showMap: showMapRaw,
  } = pickupLocationSettings || {};

  const showMoreInfoLink =
    typeof showMoreInfoLinkRaw === 'boolean' ? showMoreInfoLinkRaw : true;
  const showMap = typeof showMapRaw === 'boolean' ? showMapRaw : true;

  const {
    locationGetDirections,
    locationHoursOpenUntil,
    locationHoursOpenFuture,
    locationMoreInfo,
  } = useTranslations();

  const {
    name,
    address,
    hours,
    geocode,
    openStatus,
    openStatusTime,
    phoneNumber,
    directionUrl,
  } = pickupLocation;

  let openStatusText;
  switch (openStatus) {
    case 'OPEN':
      openStatusText = mustache.render(locationHoursOpenUntil || '', {
        closeTime: openStatusTime,
      });
      break;
    case 'CLOSED':
      openStatusText = mustache.render(locationHoursOpenFuture || '', {
        openDayAndTime: openStatusTime,
      });
      break;
    default:
      openStatusText = '';
  }

  return (
    <div
      className={
        landscape ? styles.rootContainerLandscape : styles.rootContainer
      }
    >
      {geocode && showMap && (
        <MaptilerMap
          featureName={featureName}
          coordinates={{
            lat: geocode.lat,
            lng: geocode.lng,
          }}
          s={landscape ? mapStylesLandscape : mapStylesPortrait}
        />
      )}
      <div className={styles.storeDetails} style={panelbody}>
        <div className={styles.storeContact}>
          {name && <MediumHeader as="p">{name}</MediumHeader>}
          {address && (
            <address className={styles.address} style={textStyle}>
              {address.street1}
              <br />
              {address.street2 && (
                <>
                  {address.street2}
                  <br />
                </>
              )}
              {`${address.city || ''}, ${address.state || ''} ${
                address.zip || ''
              }`}
            </address>
          )}
          {phoneNumber && (
            <TextLink
              href={`tel:${phoneNumber}`}
              linkStyle={{
                sDefault: {
                  ...linkDefault,
                  marginTop: '0.5rem',
                  display: 'inline-block',
                },
                sHover: linkHover,
              }}
              onClick={(): void =>
                logEvent({
                  clickCategory: NarvarClickSubTypes.EXTERNAL_LINK,
                  clickLinkDestinationUrl: `tel:${phoneNumber}`,
                  clickLinkLabel: phoneNumber,
                  clickSubType: NarvarClickSubTypes.PICKUP_LOCATION_PHONE_LINK,
                  clickType: NarvarClickTypes.OUTGOING,
                  eventComponent:
                    NarvarEventComponents.NTH_PACK_PICKUP_LOCATION,
                  eventFeature: NarvarEventComponents.NTH_PACK_PICKUP_LOCATION,
                  eventType: NarvarEventTypes.CLICK,
                  feature: featureName,
                  pageComponent: NarvarEventComponents.NTH_PACK_PICKUP_LOCATION,
                })
              }
            >
              {phoneNumber}
            </TextLink>
          )}
        </div>
        <div
          className={landscape ? styles.storeHoursLandscape : styles.storeHours}
        >
          {openStatusText && (
            <div style={{ ...textStyle, fontWeight: 'bold' }}>
              {openStatusText}
            </div>
          )}
          <PickupLocationHoursDisclosure
            hours={hours}
            featureName={featureName}
          />
          {directionUrl && (
            <TextLink
              href={directionUrl}
              linkStyle={{
                sDefault: {
                  ...linkSecondaryDefault,
                  fontWeight: 'bold',
                  margin: '1rem 1rem 0 0',
                  display: 'inline-block',
                  wordBreak: 'break-all',
                  textDecorationLine: 'none',
                },
                sHover: { ...linkSecondaryHover, fontWeight: 'bold' },
              }}
              onClick={(): void =>
                logEvent({
                  clickCategory: NarvarClickSubTypes.EXTERNAL_LINK,
                  clickLinkDestinationUrl: directionUrl,
                  clickLinkLabel: locationGetDirections,
                  clickSubType:
                    NarvarClickSubTypes.PICKUP_LOCATION_DIRECTIONS_LINK,
                  clickType: NarvarClickTypes.OUTGOING,
                  eventComponent:
                    NarvarEventComponents.NTH_PACK_PICKUP_LOCATION,
                  eventFeature: NarvarEventComponents.NTH_PACK_PICKUP_LOCATION,
                  eventType: NarvarEventTypes.CLICK,
                  feature: featureName,
                  pageComponent: NarvarEventComponents.NTH_PACK_PICKUP_LOCATION,
                })
              }
            >
              {locationGetDirections}
            </TextLink>
          )}
          {showMoreInfoLink && moreInfoLink && (
            <TextLink
              href={moreInfoLink}
              linkStyle={{
                sDefault: {
                  ...linkSecondaryDefault,
                  fontWeight: 'bold',
                  marginTop: '1rem',
                  display: 'inline-block',
                  wordBreak: 'break-all',
                  textDecorationLine: 'none',
                },
                sHover: { ...linkSecondaryHover, fontWeight: 'bold' },
              }}
            >
              {locationMoreInfo}
            </TextLink>
          )}
        </div>
      </div>
    </div>
  );
};

export default PickupLocationDetails;
